import React, { useState, useRef } from "react";
import { Navigate, useSearchParams, Link as RouterLink } from "react-router-dom";
import { Box, Link, Typography } from "@mui/material";
import { useAuth } from "@healthdesk/shared-hooks";
import Loading from "components/Loading";
import PhoneAuthFlow from "components/auth/PhoneAuthFlow";
import logoIcon from "assets/images/logos/logo-icon.png";

/** Allow only same-origin path (starts with /, no protocol). */
function getSafeReturnTo(returnTo) {
  if (typeof returnTo !== "string" || !returnTo.trim()) return null;
  // Collapse leading slashes so "//dashboard/..." becomes "/dashboard/..." — otherwise
  // React Router / history treat "//..." as a protocol-relative URL (SecurityError on replaceState).
  const path = returnTo.trim().replace(/^\/+/, "/");
  if (!path.startsWith("/") || path.includes("://") || path.includes(":")) return null;
  return path;
}

/**
 * PhoneAuthPage - Unified phone-based authentication
 *
 * This page handles both login and registration via phone number.
 * Supports ?role=patient|provider|facility param to pre-select account type.
 * Supports ?returnTo=/path to redirect after sign-in (e.g. /symptomchecker, /chartmind).
 */
const PhoneAuthPage = () => {
  const { user, userData, userLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const preselectedRole = searchParams.get("role");
  const returnTo = getSafeReturnTo(searchParams.get("returnTo"));

  // Track if we're actively in signup flow - prevents redirect race condition
  const signupInProgressRef = useRef(false);
  // Track if we're on SUCCESS step - hides back link
  const [isOnSuccessStep, setIsOnSuccessStep] = useState(false);

  // If user is already authenticated and has completed profile, redirect to returnTo or dashboard
  // BUT only if we're not in the middle of signup (prevents race condition)
  if (user && userData?.role && !userData?.onboarding && !signupInProgressRef.current) {
    return <Navigate to={returnTo || "/dashboard"} replace />;
  }

  // If user is authenticated but needs onboarding (e.g., facility)
  // BUT only if we're not in the middle of signup
  if (user && userData?.onboarding && !signupInProgressRef.current) {
    return <Navigate to="/onboarding" replace />;
  }

  // Show loading while checking auth state
  if (userLoading) {
    return <Loading />;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.paper",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        pt: 4,
      }}
    >
      {/* Logo - clickable only if not on success step */}
      {isOnSuccessStep ? (
        <Box
          component="img"
          src={logoIcon}
          alt="HealthDesk"
          sx={{ height: 48, mb: 2 }}
        />
      ) : (
        <Link component={RouterLink} to="/">
          <Box
            component="img"
            src={logoIcon}
            alt="HealthDesk"
            sx={{ height: 48, mb: 2 }}
          />
        </Link>
      )}

      {/* Auth flow - handles its own container width/padding */}
      <PhoneAuthFlow
        preselectedRole={preselectedRole}
        returnTo={returnTo}
        onSignupStart={() => {
          signupInProgressRef.current = true;
        }}
        onSuccessStep={setIsOnSuccessStep}
      />

      {/* Back to map link - hidden on success step */}
      {!isOnSuccessStep && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 2, mb: 4, textAlign: "center" }}
        >
          <Link
            component={RouterLink}
            to="/"
            color="inherit"
            underline="hover"
          >
            ← Back to CareMap
          </Link>
        </Typography>
      )}
    </Box>
  );
};

export default PhoneAuthPage;
